
import { Game } from "@/data/Game";
import AbstractVue from "@/views/AbstractVue";
import { gsap } from "gsap";
import { Options } from "vue-class-component";

@Options({
  props: ["frameKey"],
  watch: {
    frameKey: function () {
      this.reset();
    },
  },
})
export default class Emulator extends AbstractVue {
  frameKey: boolean;
  output = "";
  isPreloadComplete = false;
  isLoadComplete = false;
  isGameReady = false;
  isGameBusy = false;
  isBonusActive = false;
  token = "";
  loadProgress = 0;
  loadingWidth = "0%";
  stake = "";
  balance = "";
  win = "";
  gameState = "";
  soundEnabled = false;
  historyOpen = false;
  paytableOpen = false;
  turboEnabled = false;
  autoPlayActive = false;
  helpOpen = false;
  paused = false;
  forceBalance = 1000;
  consoleColors = {
    toWrapper: "background: #ffdab9; color: #772e25",
    fromWrapper: "background: #f8ad9d; color: #772e25",
  };

  mounted(): void {
    window.addEventListener("message", this.handleMessage, false);
  }

  unmounted(): void {
    window.removeEventListener("message", this.handleMessage, false);
  }

  getLoadingWidth(): string {
    return this.loadProgress + "%";
  }

  reset(): void {
    this.isPreloadComplete = false;
    this.isLoadComplete = false;
    this.isGameReady = false;
    this.isGameBusy = false;
    this.isBonusActive = false;
    this.token = "";
    this.loadProgress = 0;
    this.stake = "";
    this.balance = "";
    this.win = "";
    this.gameState = "";
    this.soundEnabled = false;
    this.historyOpen = false;
    this.paytableOpen = false;
    this.turboEnabled = false;
    this.autoPlayActive = false;
    this.output = "";
  }

  handleMessage(event: MessageEvent): void {
    try {
      if (event.data) {
        var messageData = JSON.parse(event.data);
        var type = messageData.type;
        var data = messageData.data;
        console.log(`%c >> Game to Bridge: `, `${this.consoleColors.toWrapper}`, messageData);
        switch (type) {
          case "gb-game-initialise":
            this.handleInitialise(data);
            break;
          case "gb-game-updateBalance":
            this.handleUpdateBalance(data);
            break;
          case "gb-game-updateProgress":
            this.handleUpdateProgress(data);
            break;
          case "gb-game-updateStake":
            this.handleUpdateStake(data);
            break;
          case "gb-game-updateWin":
            this.handleUpdateWin(data);
            break;
          case "gb-game-preloaded":
            this.handlePreloaded();
            break;
          case "gb-game-loaded":
            this.handleLoaded();
            break;
          case "gb-game-gameReady":
            this.handleGameReady();
            break;
          case "gb-game-gameStart":
            this.handleGameStart();
            break;
          case "gb-game-gameBusy":
            this.handleGameBusy();
            break;
          case "gb-game-gameIdle":
            this.handleGameIdle();
            break;
          case "gb-game-gameComplete":
            this.handleGameComplete();
            break;
          case "gb-game-gameRecovery":
            this.handleGameRecovery();
            break;
          case "gb-game-exitGame":
            this.handleExitGame();
            break;
          case "gb-game-deposit":
            this.handleDeposit();
            break;
          case "gb-game-gameHistoryOpen":
            this.handleGameHistory();
            break;
          case "gb-game-gameHistoryClose":
            this.handleGameHistoryClose();
            break;
          case "gb-game-soundOn":
            this.handleSoundOn();
            break;
          case "gb-game-soundOff":
            this.handleSoundOff();
            break;
          case "gb-game-turboOn":
            this.handleTurboOn();
            break;
          case "gb-game-turboOff":
            this.handleTurboOff();
            break;
          case "gb-game-helpOpen":
            this.handleHelpOpen();
            break;
          case "gb-game-helpClose":
            this.handleHelpClose();
            break;
          case "gb-game-paytableOpen":
            this.handlePaytable();
            break;
          case "gb-game-paytableClose":
            this.handlePaytableClose();
            break;
          case "gb-game-autoPlayStart":
            this.handleAutoPlayStart();
            break;
          case "gb-game-autoPlayProgress":
            this.handleAutoPlayProgress(data);
            break;
          case "gb-game-autoPlayStop":
            this.handleAutoPlayStop();
            break;
          case "gb-game-autoPlayComplete":
            this.handleAutoPlayComplete();
            break;
          case "gb-game-refresh":
            this.handleRefresh();
            break;
          case "gb-game-bonusStart":
            this.handleBonusStart();
            break;
          case "gb-game-bonusComplete":
            this.handleBonusComplete();
            break;
          case "gb-game-autoPlayOpen":
            this.logGame("Auto Play Open");
            break;
          case "gb-game-optionsOpen":
            this.logGame("Options Open");
            break;
          case "gb-game-error":
            this.logGame("Error Occurred: " + data.type);
            break;
        }
      }
    } catch (error) {
      // console.log("Error parsing message: ", error);
    }
  }

  backToGame(): void {
    this.$emit("back-to-game");
  }

  /* From Game */

  handleInitialise(data: string): void {
    this.token = data;
    this.logGame("IGame Successfully Registered" + data);
  }

  handleUpdateBalance(data: number): void {
    this.balance = data.toFixed(2);
    this.logGame("Update Balance: " + data);
  }

  handleUpdateProgress(data: number): void {
    this.loadProgress = data;
  }

  handleUpdateStake(data: number): void {
    this.stake = data.toFixed(2);
    this.logGame("Update Stake: " + data);
  }

  handleUpdateWin(data: number): void {
    this.win = data.toFixed(2);
    this.logGame("Update Win: " + data);
  }

  handlePreloaded(): void {
    this.isPreloadComplete = true;
    this.logGame("Preload Complete");
  }

  handleLoaded(): void {
    this.isLoadComplete = true;
    this.logGame("Load Complete");
  }

  handleGameReady(): void {
    this.isGameReady = true;
    this.gameState = "READY";
    this.logGame("Game Ready");
  }

  handleGameStart(): void {
    this.logGame("Game Start");
    this.gameState = "START";
  }

  handleGameBusy(): void {
    this.logGame("Game Busy");
    this.isGameBusy = true;
  }

  handleGameIdle(): void {
    this.logGame("Game Idle");
    this.isGameBusy = false;
  }

  handleGameComplete(): void {
    this.logGame("Game Complete");
    this.gameState = "COMPLETE";
  }

  handleGameRecovery(): void {
    this.logGame("Game Recovery");
    this.gameState = "RECOVERY";
  }

  handleExitGame(): void {
    this.logGame("Exit Game");
  }

  handleDeposit(): void {
    this.logGame("Deposit");
  }

  handleGameHistory(): void {
    this.historyOpen = true;
    this.logGame("Game History");
  }

  handleGameHistoryClose(): void {
    this.historyOpen = false;
    this.logGame("Game History Close");
  }

  handlePaytable(): void {
    this.paytableOpen = true;
    this.logGame("Paytable Open");
  }

  handlePaytableClose(): void {
    this.paytableOpen = false;
    this.logGame("Paytable Close");
  }

  handleSoundOn(): void {
    this.soundEnabled = true;
    this.logGame("Sound Enabled");
  }

  handleTurboOn(): void {
    this.turboEnabled = true;
    this.logGame("Turbo Enabled");
  }

  handleTurboOff(): void {
    this.turboEnabled = false;
    this.logGame("Turbo Disabled");
  }

  handleHelpOpen(): void {
    this.helpOpen = true;
    this.logGame("Help Open");
  }

  handleHelpClose(): void {
    this.helpOpen = false;
    this.logGame("Help Close");
  }

  handleSoundOff(): void {
    this.soundEnabled = false;
    this.logGame("Sound Disabled");
  }

  handleAutoPlayStart(): void {
    this.autoPlayActive = true;
    this.logGame("Auto Play Start");
  }

  handleAutoPlayProgress(data: string): void {
    this.logGame("Auto Play Progress: " + data);
  }

  handleAutoPlayStop(): void {
    this.logGame("Auto Play Stop: ");
  }

  handleAutoPlayComplete(): void {
    this.autoPlayActive = false;
    this.logGame("Auto Play Complete: ");
  }

  handleRefresh(): void {
    this.logGame("Refresh");
  }

  handleBonusStart(): void {
    this.isBonusActive = true;
    this.logGame("Bonus Start");
    this.gameState = "Bonus Start";
  }

  handleBonusComplete(): void {
    this.isBonusActive = false;
    this.logGame("Bonus Complete");
    this.gameState = "Bonus Complete";
  }

  /* To Game */

  soundOn(): void {
    this.soundEnabled = true;
    this.postToGame({ type: "gb-bridge-soundOn" });
    this.logBridge("Sound On");
  }

  soundOff(): void {
    this.soundEnabled = false;
    this.postToGame({ type: "gb-bridge-soundOff" });
    this.logBridge("Sound Off");
  }

  turboOn(): void {
    this.turboEnabled = true;
    this.postToGame({ type: "gb-bridge-turboOn" });
    this.logBridge("Turbo On");
  }

  turboOff(): void {
    this.turboEnabled = false;
    this.postToGame({ type: "gb-bridge-turboOff" });
    this.logBridge("Turbo Off");
  }

  openHelp(): void {
    this.helpOpen = true;
    this.postToGame({ type: "gb-bridge-helpOpen" });
    this.logBridge("Open Help");
  }

  closeHelp(): void {
    this.helpOpen = false;
    this.postToGame({ type: "gb-bridge-helpClose" });
    this.logBridge("Close Help");
  }

  openHistory(): void {
    this.historyOpen = true;
    this.postToGame({ type: "gb-bridge-historyOpen" });
    this.logBridge("Open History");
  }

  closeHistory(): void {
    this.historyOpen = false;
    this.postToGame({ type: "gb-bridge-historyClose" });
    this.logBridge("Close History");
  }

  openPaytable(): void {
    this.paytableOpen = true;
    this.postToGame({ type: "gb-bridge-paytableOpen" });
    this.logBridge("Open Paytable");
  }

  closePaytable(): void {
    this.paytableOpen = false;
    this.postToGame({ type: "gb-bridge-paytableClose" });
    this.logBridge("Close Paytable");
  }

  pause(): void {
    this.paused = true;
    this.postToGame({ type: "gb-bridge-pause" });
    this.logBridge("Pause");
  }

  resume(): void {
    this.paused = false;
    this.postToGame({ type: "gb-bridge-resume" });
    this.logBridge("Resume");
  }

  autoPlayStop(): void {
    this.autoPlayActive = false;
    this.postToGame({ type: "gb-bridge-autoPlayStop" });
    this.logBridge("Auto Play Stop");
  }

  updateBalance(): void {
    const request = {
      playerId: this.parameters.krakenPlayerId,
      amount: this.forceBalance,
    };
    const url: string =
      this.parameters.environment.serverAddress +
      this.parameters.game.serverPath +
      "setBalance";
    this.axios
      .post(url, request)
      .then(() => {
        this.postToGame({
          type: "gb-bridge-updateBalance",
          data: this.forceBalance,
        });
        this.logBridge("Update Balance: " + this.forceBalance);
      })
      .catch(() => {
        this.$emit("update-balance-fail");
        this.logBridge("Failed to update balance");
      });
  }

  sendError(errorType: string): void {
    this.postToGame({
      type: "gb-bridge-customError",
      data: errorType,
    });
    this.logBridge("Send Error: " + errorType);
  }

  logBridge(message: string): void {
    this.log("Emulator: " + message);
  }

  logGame(message: string): void {
    this.log("Game: " + message);
  }

  log(message: string): void {
    this.output += this.getTimestamp() + message + "\n";
  }

  getTimestamp(): string {
    var currentdate = new Date();
    return (
      this.formatTime(currentdate.getHours()) +
      ":" +
      this.formatTime(currentdate.getMinutes()) +
      ":" +
      this.formatTime(currentdate.getSeconds()) +
      ": "
    );
  }

  formatTime(value: number): string {
    return value < 10 ? "0" + value : value.toString();
  }

  postToGame(data: unknown): void {
    let gameFrame: HTMLIFrameElement = document.getElementById("gameFrame") as HTMLIFrameElement;
    if (gameFrame) {
      let target = gameFrame.contentWindow;
      if (target) {
        let message = JSON.stringify(data);
        target.postMessage(message, "*");
      }
      console.log(`%c >> Bridge to Game: `, `${this.consoleColors.fromWrapper}`, data);
    }
  }

  beforeEnter(el: HTMLElement): void {
    el.style.opacity = "0";
    el.style.transform = "translateY(200px)";
  }

  enter(el: HTMLElement, done: () => void): void {
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      onComplete: done,
      delay: 0.2 * +el.dataset.index,
    });
  }

  get gameOptions(): Game[] {
    if (this.isPrototype) {
      return [this.parameters.game];
    } else {
      return this.games;
    }
  }
}
